import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../../service/auth.service";
import { MenuItem, MessageService } from "primeng/api";
import { TranslocoService } from "@ngneat/transloco";
import { Project } from "src/app/model/form/project";
import { Title } from "@angular/platform-browser";
import { ProjectService } from "../../../service/project.service";
import { Vehicle } from "../../../model/vehicle";
import { Image } from "../../../model/Image";
import { AccountService } from "src/app/service/account.service";
import { Account } from "src/app/model/form/account";
import { TasksService } from "src/app/service/tasks.service";
import { PublicService } from "src/app/public/service.service";
import { GetAllPublicProjectsResponse } from "src/app/public/public-project-response";
import { ProjectSummaryResponse } from "src/app/model/response/ProjectSummaryResponse";
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { WelcomeTourService } from 'src/app/service/welcome-tour.service';

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.css",
})
export class DashboardComponent implements OnInit {
  token: string | null = "";
  modalVisible: boolean;
  showWelcomeDialog: boolean;
  private tourAfterWelcome = false;
  showVcuIdentifiedDialog: boolean;
  showProjectDialog: boolean;
  sidebarVisible: boolean;
  items: MenuItem[] | undefined;
  projects: Project[];
  projectsBackup: Project[];
  sharedProjects: ProjectSummaryResponse[];
  sharedProjectsBackup: Project[];
  singleProjectDetail: Project | null = null;
  projectDetails: (Project | null)[] = [];
  vehicle: Vehicle = new Vehicle();
  image: Image = new Image();
  userID: string = "";
  filterValue: string = "";
  isLoading: boolean = false;
  isLoadingShared: boolean = false;
  showReactiveAccountDialog: boolean;
  reactiveAccountMessage: string = "";

  favoriteProjects: GetAllPublicProjectsResponse[] = [];
  mostLikrdProjects: GetAllPublicProjectsResponse[] = [];

  projectsPerPage = 3;
  currentIndex = 0;
  currentIndexShared = 0;

  selectedTab: "garage" | "vcus" | "sharedGarage" = "garage";
  selectedTabFavorite: "destaque" | "favorite" = "destaque";

  showSearch = false;
  searchText = "";

  modalEvent: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private titleService: Title,
    private translocoService: TranslocoService,
    private projectService: ProjectService,
    private accountService: AccountService,
    private messageService: MessageService,
    private tasksService: TasksService,
    private publicService: PublicService,
    private router: Router,
    private welcomeTourService: WelcomeTourService,
  ) {
    this.modalVisible = true;
    this.showWelcomeDialog = false;
    this.showVcuIdentifiedDialog = false;
    this.showProjectDialog = false;
    this.sidebarVisible = false;
    this.projects = [];
    this.sharedProjects = [];
    this.projectsBackup = [];
    this.sharedProjectsBackup = [];
    this.isLoading = false;
    this.isLoadingShared = false;
    this.showReactiveAccountDialog = false;
  }

  verify() {
    let token = sessionStorage.getItem("token");
    if (token) this.authService.verifyToken(token);
  }
  refresh() {
    let refreshToken = sessionStorage.getItem("refreshToken");
    if (refreshToken) this.authService.refreshAuth();
  }
  revoke() {
    let token = sessionStorage.getItem("refreshToken");
    if (token) this.authService.revokeToken(token);
  }

  ngOnInit() {
    this.token = sessionStorage.getItem("token");
    this.changeLanguage();
    this.setTitle();
    
    // Mostrar loading imediatamente
    this.isLoading = true;
    this.isLoadingShared = true;

    // Carregar dados sem bloquear navegação
    this.loadDashboardData();

    this.activatedRoute.fragment.subscribe((fragment: string | null) => {
      if (fragment) this.jumpToSection(fragment);
    });

    this.modalEvent = this.checkDateAndTime();
  }

  /**
   * Carrega dados do dashboard de forma otimizada com loading imediato
   */
  private loadDashboardData(): void {
    // Buscar userData do cache (AuthService já tem)
    this.authService.getUserData().pipe(
      switchMap((userData) => {
        if (!userData) {
          this.router.navigate(['/login']);
          throw new Error('Usuário não autenticado');
        }

        this.userID = userData.userID;

        // Carregar projetos e projetos compartilhados em paralelo
        return forkJoin({
          userData: of(userData),
          projects: this.projectService.findAllProjectSummary(
            userData.userID,
            "AC",
            "AC,AP"
          ).pipe(catchError((error) => {
            console.error('Erro ao carregar projetos:', error);
            return of([]);
          })),
          sharedProjects: this.projectService.FindProjectsSharedSummary(
            userData.userID
          ).pipe(catchError((error) => {
            console.error('Erro ao carregar projetos compartilhados:', error);
            return of([]);
          }))
        });
      }),
      catchError((error) => {
        console.error('Erro ao carregar dados do dashboard:', error);
        this.isLoading = false;
        this.isLoadingShared = false;
        return of(null);
      })
    ).subscribe((result) => {
      if (result) {
        // Processar projetos
        if (result.projects && result.projects.length > 0) {
          this.projects = this.projectService.projectSummaryToProject(result.projects);
          this.projectsBackup = this.projectService.projectSummaryToProject(result.projects);

          // Buscar detalhes completos para o painel de resumo (1 ou 2 projetos)
          if (this.projects.length === 1) {
            this.loadSingleProjectDetail(this.projects[0].id);
          } else if (this.projects.length === 2) {
            this.loadProjectDetails([this.projects[0].id, this.projects[1].id]);
          }
        }

        // Processar projetos compartilhados
        if (result.sharedProjects && result.sharedProjects.length > 0) {
          this.sharedProjects = result.sharedProjects;
        }

        this.isLoading = false;
        this.isLoadingShared = false;

        // Carregar dados secundários em background
        this.loadSecondaryData(result.userData);
      }
    });
  }

  /**
   * Carrega dados secundários sem bloquear UI principal
   */
  private loadSecondaryData(userData: any): void {
    // Verificar primeiro acesso
    if (userData.firstAccess) {
      this.showWelcomeDialog = true;
      this.authService.markFirstAccessDone();
      this.updateFirstAccessFlag(userData.userID);
    }

    // Verificar dias restantes (não crítico, não bloqueia)
    this.accountService.getAccount(this.userID).subscribe({
      next: (response) => {
        if (response.daysLeft) {
          this.showReactiveAccountDialog = true;
          this.translocoService
            .selectTranslateObject("user.account.reactive")
            .subscribe((value) => {
              this.reactiveAccountMessage = `${value.messageOne} ${response.daysLeft} ${value.messageTwo}`;
            })
            .unsubscribe();
        }
      },
      error: (error) => {
        // Falha silenciosa - não é crítico
        console.warn('Erro ao verificar dias restantes:', error);
      }
    });
  }

  /**
   * Atualiza flag de primeiro acesso
   */
  private updateFirstAccessFlag(userID: string): void {
    this.accountService.getAccount(userID).subscribe((userData) => {
      let account: Account = AccountService.toAccount(userData);
      account.firstAccess = false;
      this.accountService.updateAccount(account).subscribe();
    });
  }

  jumpToSection(section: string | null) {
    if (section)
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
  }

  addVcu() {
    this.showVcuIdentifiedDialog = true;
  }
  updateWelcomeDialog() {
    this.showWelcomeDialog = false;
  }
  updateVcuIdentifiedDialog() {
    this.showVcuIdentifiedDialog = false;
    this.projectService.clearProjectsCache(this.userID);
    this.findAllProjectSummary(this.userID, true);
  }
  addProject() {
    // Garantir que o dialog está fechado antes de abrir novamente
    this.showProjectDialog = false;
    setTimeout(() => {
      this.showProjectDialog = true;
    }, 0);
  }
  updateProjectDialog() {
    // Apenas fecha — NÃO recarrega. O reload só acontece quando o projeto é salvo (onProjectSaved).
    this.showProjectDialog = false;
  }

  /** Chamado pelo (saved) do modal — recarrega a lista só quando houve criação/alteração salva. */
  onProjectSaved() {
    this.showProjectDialog = false;
    this.projectService.clearProjectsCache(this.userID);
    this.findAllProjectSummary(this.userID, true);
  }

  closeModal() {
    this.showWelcomeDialog = false;
  }

  /** "Fazer o tour" na modal de boas-vindas: o tour começa quando a modal termina de fechar. */
  takeTourFromWelcome(): void {
    this.tourAfterWelcome = true;
    this.showWelcomeDialog = false;
  }

  /** (onHide) roda depois da animação de saída — a máscara da modal já saiu da frente do tour. */
  onWelcomeHidden(): void {
    this.showWelcomeDialog = false;
    if (!this.tourAfterWelcome) return;
    this.tourAfterWelcome = false;
    this.welcomeTourService.start();
  }

  setTitle() {
    this.translocoService
      .selectTranslate("title.dashboard")
      .subscribe((value) => this.titleService.setTitle(value));
  }

  public changeLanguage(languageCode?: string | null): void {
    if (!languageCode || languageCode === null)
      languageCode = sessionStorage.getItem("language");
    if (languageCode && languageCode !== null)
      this.translocoService.setActiveLang(languageCode);
  }

  findAllProjectSummary(userID: string | null, forceRefresh: boolean = false) {
    if (userID === null) return;
    this.isLoading = true;
    this.projectService.findAllProjectSummary(userID, "AC", "AC,AP", forceRefresh).subscribe({
      next: (data) => {
        if (data) {
          this.projects = this.projectService.projectSummaryToProject(data);
          this.projectsBackup =
            this.projectService.projectSummaryToProject(data);
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
      },
    });
  }

  findAllSharedProjectSummary(userID: string | null, forceRefresh: boolean = false) {
    if (userID === null) return;
    this.isLoadingShared = true;
    this.projectService.FindProjectsSharedSummary(userID, forceRefresh).subscribe({
      next: (response) => {
        this.isLoadingShared = false;
        if (response) {
          this.sharedProjects = response;
          //   this.projectService.projectSummaryToProject(response);
        }
      },
      error: (_) => {
        this.isLoadingShared = false;
      },
    });
  }

  applyFilter() {
    let filter: string = this.filterValue.trim().toLowerCase();
    this.projects = this.projectsBackup;
    if (filter !== "") {
      this.projects = this.projectsBackup.filter((p) =>
        p.name.toLowerCase().includes(filter)
      );
    }
  }

  applyFilterShared() {
    let filter: string = this.filterValue.trim().toLowerCase();
    this.projects = this.projectsBackup;
    if (filter !== "") {
      this.projects = this.projectsBackup.filter((p) =>
        p.name.toLowerCase().includes(filter)
      );
    }
  }

  refreshList() {
    this.userID = this.authService.getUserInformations();
    
    // Limpar caches antes de buscar novos dados
    this.projectService.clearProjectsCache(this.userID);
    this.authService.clearUserCache();
    
    // Indicar que está carregando
    this.isLoading = true;
    this.isLoadingShared = true;

    // Forçar refresh dos dados em paralelo
    forkJoin({
      projects: this.projectService.findAllProjectSummary(
        this.userID, 
        "AC", 
        "AC,AP", 
        true
      ).pipe(catchError(() => of([]))),
      sharedProjects: this.projectService.FindProjectsSharedSummary(
        this.userID, 
        true
      ).pipe(catchError(() => of([])))
    }).subscribe({
      next: (result) => {
        // Processar projetos
        if (result.projects) {
          this.projects = this.projectService.projectSummaryToProject(result.projects);
          this.projectsBackup = this.projectService.projectSummaryToProject(result.projects);
        }
        
        // Processar projetos compartilhados
        if (result.sharedProjects) {
          this.sharedProjects = result.sharedProjects;
        }
        
        this.isLoading = false;
        this.isLoadingShared = false;
      },
      error: (error) => {
        console.error('Erro ao atualizar lista:', error);
        this.isLoading = false;
        this.isLoadingShared = false;
      }
    });
  }

  closeModalReactiveAccount() {
    this.showReactiveAccountDialog = false;
  }

  ReactiveAccount() {
    this.translocoService
      .selectTranslateObject("user.account.reactive")
      .subscribe((value) => {
        let account = new Account();
        account.id = this.userID;
        account.inactive = false;
        this.messageService.clear();
        this.accountService.updateAccount(account).subscribe({
          next: (_) => {
            this.showReactiveAccountDialog = false;
            this.messageService.add({
              key: "toastDashboard",
              severity: "success",
              summary: value.header,
              detail: value.success,
            });
          },
          error: (_) => {
            setTimeout(() => {
              this.messageService.clear();
              this.messageService.add({
                key: "toastDashboard",
                severity: "error",
                summary: value.header,
                detail: value.error,
              });
            }, 10000);
            this.logoutAccount();
          },
        });
      })
      .unsubscribe();
  }

  logoutAccount() {
    this.authService.logout();
  }

  selectTab(tab: "garage" | "vcus" | "sharedGarage") {
    this.currentIndex = 0;
    this.currentIndexShared = 0;
    this.selectedTab = tab;
  }

  selectTabFavorite(tab: "favorite" | "destaque") {
    this.currentIndex = 0;
    this.currentIndexShared = 0;
    this.selectedTabFavorite = tab;
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
  }

  getFavoriteProjects(userID: string, page: number, itensPage: number) {
    if (this.userID) {
      this.publicService.getFavoriteProject(userID, page, itensPage).subscribe({
        next: (data) => {
          this.favoriteProjects = Array.isArray(data.listItens)
            ? data.listItens
            : [data.listItens];
        },
        error: (error) => {},
      });
    }
  }

  getMostLikedProjects() {
    if (this.userID) {
      const sharingProjectType = 1;
      this.publicService
        .getAllPublicProjects(sharingProjectType, null, 3)
        .subscribe({
          next: (data) => {
            this.mostLikrdProjects = Array.isArray(data.listItens)
              ? data.listItens.slice(0, 3)
              : [data.listItens];
          },
          error: (error) => {},
        });
    }
  }

  checkDateAndTime(): boolean {
    const statusEvent = localStorage.getItem("modalEvent");
    if (statusEvent == "0") {
      return false;
    } else {
      const modalEvent = false;

      const targetDate = new Date("2025-09-21T12:00:00-03:00"); // 21/09/2025 às 12h (horário de Brasília)
      const now = new Date();

      const isSameDay =
        now.getDate() === targetDate.getDate() &&
        now.getMonth() === targetDate.getMonth() &&
        now.getFullYear() === targetDate.getFullYear();

      const isBeforeNoon = now.getHours() < 12;

      if (isSameDay && isBeforeNoon) {
        return true; // modalEvent = true
      }

      return false;
    }
  }

  closeModalEvent() {
    this.modalEvent = false;
    localStorage.setItem("modalEvent", "0");
  }
  goToEvent() {
    this.router.navigate(["/events"]);
  }

  private loadSingleProjectDetail(projectId: string): void {
    this.projectService.getProject(this.userID, projectId).subscribe({
      next: (response) => {
        this.singleProjectDetail = ProjectService.toProject(response);
      },
      error: () => {}
    });
  }

  private loadProjectDetails(ids: string[]): void {
    this.projectDetails = new Array(ids.length).fill(null);
    ids.forEach((id, i) => {
      this.projectService.getProject(this.userID, id).subscribe({
        next: (response) => {
          this.projectDetails[i] = ProjectService.toProject(response);
        },
        error: () => {}
      });
    });
  }

  goToProjectDetail(project: Project): void {
    this.router.navigate(['/project', project.id]);
  }

  goToVcuManagement(): void {
    this.selectTab('vcus');
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}
